import moment from 'moment-timezone';
import 'moment/locale/nl';

// Alle reserveringstijden horen bij deze zone. In de database staan de tijden
// als absolute UTC-momenten; deze helper is de enige plek waar we bepalen hoe
// die momenten naar de klok van de gebruiker (Amsterdam) worden vertaald.
export const AMSTERDAM_TZ = 'Europe/Amsterdam';

/**
 * Geef een opgeslagen moment terug als moment-object in Amsterdamse tijd,
 * om verder mee te rekenen of te formatteren.
 */
export const toAmsterdam = (value: moment.MomentInput): moment.Moment =>
  moment.tz(value, AMSTERDAM_TZ);

/**
 * Interpreteer 'YYYY-MM-DD HH:mm' als Amsterdamse kloktijd (bv. openingstijden).
 */
export const amsterdamDateTime = (
  value: string,
  format = 'YYYY-MM-DD HH:mm'
): moment.Moment => moment.tz(value, format, AMSTERDAM_TZ);

/**
 * Toon een opgeslagen moment in Amsterdamse tijd (Nederlandse maand-/dagnamen).
 */
export const formatAmsterdam = (value: moment.MomentInput, format: string): string =>
  moment.tz(value, AMSTERDAM_TZ).locale('nl').format(format);

/** Harde bovengrens van de server op het aantal keren in een reeks. */
export const MAX_OCCURRENCES = 52;

export interface Occurrence {
  start_at: string;
  end_at: string;
}

/**
 * De datums van een wekelijkse herhaling. Rekent in Amsterdamse tijd met stappen
 * van zeven dagen, gelijk aan `weeklyOccurrences` in de backend, zodat een
 * afspraak van 14:00 ook na de overgang naar wintertijd om 14:00 begint.
 */
export const weeklyOccurrences = (
  startValue: moment.MomentInput,
  endValue: moment.MomentInput,
  count: number
): Occurrence[] => {
  const start = toAmsterdam(startValue);
  const end = toAmsterdam(endValue);
  if (!start.isValid() || !end.isValid() || !Number.isInteger(count) || count < 1) {
    return [];
  }

  return Array.from({ length: count }, (_unused, index) => ({
    start_at: start.clone().add(index * 7, 'days').toISOString(),
    end_at: end.clone().add(index * 7, 'days').toISOString(),
  }));
};

/**
 * Hoeveel keren er bij deze startdatum nog binnen de termijn van de locatie
 * passen. Spiegelt de controle van de server: de laatste keer mag vallen op de
 * dag `vandaag + maxWeeks`, hoe laat op die dag dan ook.
 */
export const maxOccurrencesFor = (startValue: moment.MomentInput, maxWeeks: number): number => {
  const start = toAmsterdam(startValue);
  if (!start.isValid() || !maxWeeks || maxWeeks < 1) return 1;

  const horizon = moment.tz(AMSTERDAM_TZ).add(maxWeeks, 'weeks').endOf('day');
  const fits = horizon.diff(start, 'weeks') + 1;

  return Math.min(MAX_OCCURRENCES, Math.max(1, fits));
};
